/**
 * EmployeeReportPage - Concise validation view for employees.
 * Shows a digestible summary of survey findings so employees can confirm accuracy.
 * Uses a donut chart for time allocation, compact role table, and simple lists.
 * Mobile-first design.
 *
 * Accessed via /employee/report/{token} — no admin auth required
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
    Loader2, AlertTriangle, Lock, User, Clock,
    ArrowRight, Wrench, CheckCircle2,
    Send, Briefcase, PieChart, BarChart2
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import '../styles/design-system.css';

const PIE_COLORS = ['#007AFF', '#5856D6', '#FF9500', '#34C759', '#FF3B30', '#AF52DE', '#FF2D55', '#5AC8FA', '#FFCC00', '#00C7BE'];

const ALIGNMENT_MAP = {
    'aligned': { bg: '#E8F5E9', color: '#2E7D32', label: 'Aligned' },
    'partially_aligned': { bg: '#FFF3E0', color: '#E65100', label: 'Partial' },
    'misaligned_role_drift': { bg: '#FFE5E5', color: '#C62828', label: 'Drift' },
    'misaligned_tm_understanding': { bg: '#E3F2FD', color: '#1565C0', label: 'Gap' },
    'not_mentioned': { bg: '#F0F0F5', color: '#86868B', label: 'N/A' },
};

const ACCURACY_OPTIONS = [
    { value: 'very_accurate', label: 'Very Accurate', desc: 'Closely matches my actual time' },
    { value: 'mostly_accurate', label: 'Mostly Accurate', desc: 'Generally correct, minor differences' },
    { value: 'somewhat_inaccurate', label: 'Somewhat Off', desc: 'Several areas don\'t match' },
    { value: 'very_inaccurate', label: 'Not Accurate', desc: 'Doesn\'t reflect my day-to-day' },
];

function buildDonutGradient(items) {
    let cum = 0;
    const stops = items.map((item, i) => {
        const start = cum;
        cum += item.percentage;
        return `${PIE_COLORS[i % PIE_COLORS.length]} ${start}% ${cum}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
}

function toolName(t) {
    return typeof t === 'string' ? t : t.tool_name || t.name || t.tool || t.desired_tool || JSON.stringify(t);
}

function EmployeeReportPage({ token }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [timeAccuracy, setTimeAccuracy] = useState('');
    const [overstated, setOverstated] = useState('');
    const [missing, setMissing] = useState('');
    const [additionalFeedback, setAdditionalFeedback] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        const loadReport = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/public/employee/report/${token}`);
                if (res.status === 403) {
                    const data = await res.json();
                    setError(data.detail || 'This link has expired or been revoked.');
                    return;
                }
                if (res.status === 404) {
                    setError('Report not found. This link may be invalid.');
                    return;
                }
                if (!res.ok) throw new Error('Failed to load report');
                const data = await res.json();
                setReport(data);
                if (data.validation_submitted) setSubmitted(true);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadReport();
    }, [token]);

    const handleSubmitValidation = async () => {
        if (!timeAccuracy) {
            setSubmitError('Please select how accurately the report reflects your time.');
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/public/employee/report/${token}/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    time_accuracy: timeAccuracy,
                    overstated_understated: overstated || null,
                    missing_responsibilities: missing || null,
                    additional_feedback: additionalFeedback || null,
                }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Failed to submit');
            }
            setSubmitted(true);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="er-page">
                <div className="er-status-card">
                    <Loader2 size={28} className="rp-spinner-icon" />
                    <p>Loading your report...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="er-page">
                <div className="er-status-card">
                    <AlertTriangle size={28} style={{ color: '#FF3B30' }} />
                    <h2>Unable to Load Report</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!report) return null;

    const data = report.report || {};
    const roles = data.role_responsibilities || [];
    const timeAlloc = data.time_allocation || [];
    const handoffs = data.handoff_map || [];
    const tech = data.technology_assessment || {};
    const challenges = data.workflow_challenges || [];
    const overlaps = data.process_overlaps || [];

    // Normalize time allocation to 100%
    const rawTotal = timeAlloc.reduce((s, t) => s + (t.percentage || 0), 0);
    const normalizedTime = rawTotal > 0 ? timeAlloc.map(t => ({
        ...t,
        percentage: Math.round((t.percentage / rawTotal) * 100)
    })) : timeAlloc;
    // Fix any rounding remainder so it sums to exactly 100
    if (normalizedTime.length > 0) {
        const normTotal = normalizedTime.reduce((s, t) => s + t.percentage, 0);
        if (normTotal !== 100) normalizedTime[0].percentage += (100 - normTotal);
    }

    // Build tools by category (deduplicated)
    const toolCategories = [
        { key: 'good', label: 'Working Well', items: (tech.tools_working_well || []).map(toolName), color: '#2E7D32', bg: '#E8F5E9' },
        { key: 'bad', label: 'Causing Friction', items: (tech.tools_causing_friction || []).map(toolName), color: '#C62828', bg: '#FFE5E5' },
        { key: 'wish', label: 'Wished For', items: (tech.tool_wishes || []).map(toolName), color: '#E65100', bg: '#FFF3E0' },
    ];
    // "In Use" tools that aren't already in good/bad
    const categorized = new Set([...toolCategories.flatMap(c => c.items)]);
    const otherTools = (tech.tools_in_use || []).map(toolName).filter(t => !categorized.has(t));
    if (otherTools.length > 0) {
        toolCategories.unshift({ key: 'use', label: 'Tools In Use', items: otherTools, color: '#424245', bg: '#F0F0F5' });
    }
    const hasTools = toolCategories.some(c => c.items.length > 0);

    const frictionItems = [
        ...challenges.map(c => c.description),
        ...overlaps.map(o => `${o.task}${o.involved_parties ? ` (involves ${Array.isArray(o.involved_parties) ? o.involved_parties.join(', ') : o.involved_parties})` : ''}`),
    ].filter(Boolean);

    const donutGradient = normalizedTime.length > 0 ? buildDonutGradient(normalizedTime) : null;

    // JD alignment — single % stat (exclude N/A, give partial half credit)
    const naCount = roles.filter(r => r.alignment === 'not_mentioned').length;
    const alignedCount = roles.filter(r => r.alignment === 'aligned').length;
    const partialCount = roles.filter(r => r.alignment === 'partially_aligned').length;
    const scoredTotal = roles.length - naCount;
    const alignmentPct = scoredTotal > 0 ? Math.round(((alignedCount + partialCount * 0.5) / scoredTotal) * 100) : null;

    return (
        <div className="er-page">
            <div className="er-container">
                {/* Header */}
                <header className="er-header">
                    <Lock size={14} className="er-header-lock" />
                    <h1>Your Work Study Summary</h1>
                    <p className="er-header-meta">
                        <User size={13} /> {report.employee_name}
                        {report.role_title && <> &bull; {report.role_title}</>}
                        &bull; {report.department}
                    </p>
                </header>

                {/* Intro */}
                <div className="er-card er-intro-card">
                    <p>
                        We've summarized what you shared during the work study survey. Please take a quick look
                        and let us know if we captured things correctly.
                    </p>
                </div>

                {/* SECTION 1: Time Spent (per Victoria — lead with this) */}
                {normalizedTime.length > 0 && (
                    <section className="er-card">
                        <h2 className="er-card-title">
                            <PieChart size={15} style={{ color: '#007AFF' }} />
                            How You Spend Your Time
                        </h2>
                        <div className="er-donut-wrap">
                            <div className="er-donut" style={{ background: donutGradient }}>
                                <div className="er-donut-hole" />
                            </div>
                            <div className="er-donut-legend">
                                {normalizedTime.map((t, i) => (
                                    <div key={i} className="er-legend-item">
                                        <span className="er-legend-swatch" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                                        <span className="er-legend-label">{t.category}</span>
                                        <span className="er-legend-pct">{t.percentage}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* SECTION 2: Workflows & Tools (reorganized with category labels) */}
                {(handoffs.length > 0 || hasTools) && (
                    <section className="er-card">
                        <h2 className="er-card-title">
                            <ArrowRight size={15} style={{ color: '#FF9500' }} />
                            Workflows &amp; Tools
                        </h2>

                        {handoffs.length > 0 && (
                            <div className="er-mini-section">
                                <h3 className="er-mini-title">Key Handoffs</h3>
                                <div className="er-handoff-list">
                                    {handoffs.map((h, i) => (
                                        <div key={i} className="er-handoff-pill">
                                            <span className="er-handoff-from">{h.from_role}</span>
                                            <ArrowRight size={12} />
                                            <span className="er-handoff-to">{h.to_role}</span>
                                            {h.frequency && <span className="er-handoff-freq-badge">{h.frequency}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {hasTools && (
                            <div className="er-mini-section">
                                <h3 className="er-mini-title">Your Tools</h3>
                                {toolCategories.filter(c => c.items.length > 0).map(cat => (
                                    <div key={cat.key} className="er-tool-category">
                                        <span className="er-tool-cat-label" style={{ color: cat.color }}>{cat.label}</span>
                                        <div className="er-tag-list">
                                            {cat.items.map((t, i) => (
                                                <span key={i} className={`er-tag er-tag-${cat.key}`}>{t}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {/* SECTION 3: Friction & Pain Points */}
                {frictionItems.length > 0 && (
                    <section className="er-card">
                        <h2 className="er-card-title">
                            <Wrench size={15} style={{ color: '#FF3B30' }} />
                            Friction &amp; Pain Points
                        </h2>
                        <ul className="er-friction-list">
                            {frictionItems.map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* SECTION 4: JD Alignment — single % stat (per Victoria — minimize focus) */}
                {roles.length > 0 && alignmentPct !== null && (
                    <section className="er-card er-jd-card">
                        <h2 className="er-card-title">
                            <BarChart2 size={15} style={{ color: '#5856D6' }} />
                            Role Alignment
                        </h2>
                        <div className="er-jd-stat-row">
                            <div className="er-jd-pct">
                                <span className="er-jd-pct-num">{alignmentPct}%</span>
                                <span className="er-jd-pct-label">of your JD responsibilities align with what you described doing</span>
                            </div>
                        </div>
                        <p className="er-jd-note">
                            Role scope and team structure will be reviewed holistically as part of this project;
                            this metric is for context only.
                        </p>
                    </section>
                )}

                {/* VALIDATION SURVEY */}
                <section className="er-card er-validation-card">
                    <h2 className="er-card-title">
                        <Send size={15} style={{ color: '#007AFF' }} />
                        Does This Look Right?
                    </h2>
                    {submitted ? (
                        <div className="er-thanks">
                            <CheckCircle2 size={24} style={{ color: '#34C759' }} />
                            <h3>Thank you!</h3>
                            <p>Your feedback helps us move forward with accurate data.</p>
                        </div>
                    ) : (
                        <div className="er-form">
                            <p className="er-form-intro">
                                Help us confirm this is accurate before we use it to develop recommendations.
                            </p>

                            {/* Q1: Time Accuracy */}
                            <div className="er-q">
                                <label className="er-q-label">
                                    1. Does this reflect how you spend your time? <span className="er-req">*</span>
                                </label>
                                <div className="er-radio-grid">
                                    {ACCURACY_OPTIONS.map(opt => (
                                        <label key={opt.value} className={`er-radio-chip ${timeAccuracy === opt.value ? 'er-radio-active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="time_accuracy"
                                                value={opt.value}
                                                checked={timeAccuracy === opt.value}
                                                onChange={e => setTimeAccuracy(e.target.value)}
                                            />
                                            <strong>{opt.label}</strong>
                                            <span>{opt.desc}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Q2 */}
                            <div className="er-q">
                                <label className="er-q-label">
                                    2. Anything overstated or understated?
                                </label>
                                <textarea
                                    className="er-input"
                                    placeholder="e.g., 'Meetings feel overstated — closer to 10%, not 25%'"
                                    value={overstated}
                                    onChange={e => setOverstated(e.target.value)}
                                    rows={2}
                                />
                            </div>

                            {/* Q3 */}
                            <div className="er-q">
                                <label className="er-q-label">
                                    3. Is anything missing?
                                </label>
                                <textarea
                                    className="er-input"
                                    placeholder="e.g., 'Vendor management (~5 hrs/wk) isn't captured here'"
                                    value={missing}
                                    onChange={e => setMissing(e.target.value)}
                                    rows={2}
                                />
                            </div>

                            {/* Q4 */}
                            <div className="er-q">
                                <label className="er-q-label">
                                    4. Anything else? <span className="er-opt">(optional)</span>
                                </label>
                                <textarea
                                    className="er-input"
                                    placeholder="Any other corrections or context..."
                                    value={additionalFeedback}
                                    onChange={e => setAdditionalFeedback(e.target.value)}
                                    rows={2}
                                />
                            </div>

                            {submitError && <p className="er-error-msg">{submitError}</p>}

                            <button className="er-submit" onClick={handleSubmitValidation} disabled={submitting}>
                                {submitting ? <Loader2 size={15} className="rp-spinner-icon" /> : <Send size={15} />}
                                {submitting ? 'Submitting...' : 'Submit Feedback'}
                            </button>
                        </div>
                    )}
                </section>

                <footer className="er-footer">
                    <p>AI Work Audit &bull; Org Scaling &amp; Systems Readiness &bull; Confidential</p>
                </footer>
            </div>
        </div>
    );
}

export default EmployeeReportPage;
